import { useApolloClient, useMutation } from "@apollo/client";
import { Breadcrumb, Dropdown } from "pepsico-ds";
import { useCallback, useContext, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { deleteKeywordGroup } from "../../graphql/mutations/deleteKeywordGroup";
import { updateTag } from "../../graphql/mutations/renameStoreGroup";
import { updateStoreGroupsStatus } from "../../graphql/mutations/updateStoreGroupsStatus";
import { updateKeywordGroupCategories } from "../../graphql/mutations/updateKeywordGroupCategories";
import { getOutletKeywordGroups } from "../../graphql/queries/getOutletKeywordGroups";
import { listCampaignsByStoreGroups } from "../../graphql/queries/listCampaignsByStoreGroups";
import { useStoreGroupsSummary } from "../../hooks/useStoreGroupsSummary";
import { useStores } from "../../hooks/useStores";
import { useToast } from "../../hooks/useToast";
import RenameStoreGroupModal from "./RenameStoreGroupModal";
import StoreFileUploadModal from "./StoreFileUploadModal";
import StoreGroupModal from "./StoreGroupModal";
import StoreGroupsListView from "./StoreGroupsListView";
import StoresListView from "./StoresListView";
import { STATUS, VIEW_OPTIONS } from "./storeConstants";
import "./storeManagement.scss";

const STORE_GROUPS_VIEW_URL = "/store-management?view=store-groups";

const storeCategories = (store) =>
  store?.categories?.length ? store.categories : [store?.category].filter(Boolean);

export default function StoreManagementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { stores, allStores, loading, searchTerm, filterData, handlers, refetch } =
    useStores();
  const {
    storeGroups,
    loading: storeGroupsLoading,
    refetch: refetchStoreGroups,
    statusFilter: groupStatusFilter,
    setStatusFilter: setGroupStatusFilter,
  } = useStoreGroupsSummary();

  const client = useApolloClient();
  const { refetchProgramConfig, programMarketConfig } =
    useContext(GlobalConfigContext);
  const isSyncMarket = useMemo(
    () => programMarketConfig?.store_sync_type === "SYNC",
    [programMarketConfig?.store_sync_type]
  );
  const { toast, showToast, closeToast } = useToast();

  const [executeUpdateCategories] = useMutation(updateKeywordGroupCategories);
  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);
  const [executeRename] = useMutation(updateTag);
  const [executeGroupStatus] = useMutation(updateStoreGroupsStatus);
  const [pendingDeactivation, setPendingDeactivation] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);

  const [activeView, setActiveView] = useState(() => [
    searchParams.get("view") === VIEW_OPTIONS[1].id
      ? VIEW_OPTIONS[1]
      : VIEW_OPTIONS[0],
  ]);
  const [viewedGroup, setViewedGroup] = useState(null);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [targetGroup, setTargetGroup] = useState(null);

  const targetGroupStatus =
    storeGroups.find((g) => g.name === targetGroup)?.status || STATUS.ACTIVE;

  const currentView = activeView?.[0]?.id || "stores";
  const groupInView =
    currentView === "stores" &&
    viewedGroup &&
    filterData?.categories?.length === 1 &&
    filterData.categories[0] === viewedGroup
      ? viewedGroup
      : null;

  const handleViewChange = (selected) => {
    setActiveView(selected);
    setViewedGroup(null);
    const viewId = selected?.[0]?.id;
    if (viewId === "store-groups") {
      refetchStoreGroups();
    }
  };

  // Navigate to create store page
  const handleAddSingleStore = () => {
    navigate("/store-management/create");
  };

  // Open import file modal
  const handleImportFile = () => {
    setShowFileUploadModal(true);
  };

  const handleViewStore = (store) => {
    navigate(`/store-management/view/${store.id}`);
  };

  // Navigate to edit store page
  const handleEditStore = (store) => {
    navigate(`/store-management/edit/${store.id}`);
  };

  // Delete a single store
  const handleDeleteStore = async (id, name) => {
    try {
      await executeDeleteKeyword({ variables: { id } });
      showToast("success", "Store Deleted", `"${name}" has been deleted`);
      if (refetch) refetch();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete the store"
      );
    }
  };

  // Bulk delete
  const handleDeleteMultiple = async (storeIds) => {
    try {
      await Promise.all(
        storeIds.map((storeId) =>
          executeDeleteKeyword({ variables: { id: storeId } })
        )
      );
      showToast("success", "Stores Deleted", `${storeIds.length} stores deleted`);
      if (refetch) refetch();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete stores"
      );
    }
  };

  const handleFileUploadSuccess = () => {
    showToast("success", "Import Completed", "Store data imported successfully");
    if (refetch) refetch();
    refetchStoreGroups();
  };

  // Store Group handlers
  const handleAddGroup = () => {
    setShowGroupModal(true);
  };

  const handleEditGroup = useCallback((groupNames) => {
    const groupName = Array.isArray(groupNames) ? groupNames[0] : groupNames;
    setTargetGroup(groupName);
    setRenameModalOpen(true);
  }, []);

  const handleRenameClose = () => {
    setRenameModalOpen(false);
    setTargetGroup(null);
  };

  const applyGroupUpdate = async (newName, status) => {
    const nameChanged = newName !== targetGroup;
    const statusChanged = status !== targetGroupStatus;
    setIsRenaming(true);
    try {
      if (statusChanged) {
        const { data } = await executeGroupStatus({
          variables: { input: { names: [targetGroup], status } },
        });
        const result = data?.updateStoreGroupsStatus;
        if (!result?.success) {
          throw new Error(result?.message || "Could not update the group status");
        }
      }

      if (nameChanged) {
        await executeRename({
          variables: { oldName: targetGroup, newName, type: "STORE_GROUPS" },
        });

        const { data } = await client.query({
          query: getOutletKeywordGroups,
          variables: { filters: { category: targetGroup } },
          fetchPolicy: "network-only",
        });

        const storeIds = (data?.listKeywordGroups || []).map((s) => s.id);
        if (storeIds.length > 0) {
          await executeUpdateCategories({
            variables: {
              input: {
                keywordGroupIds: storeIds,
                type: "OUTLET",
                categories: newName,
              },
            },
          });
        }
      }

      showToast(
        "success",
        nameChanged ? "Group Renamed" : "Group Updated",
        nameChanged
          ? `"${targetGroup}" has been renamed to "${newName}"`
          : `"${targetGroup}" status has been updated`
      );
      if (refetchProgramConfig) refetchProgramConfig();
      if (refetchStoreGroups) refetchStoreGroups();
      handleRenameClose();
    } catch (error) {
      showToast(
        "error",
        statusChanged && !nameChanged ? "Status Update Failed" : "Rename Failed",
        error.message || "Could not update the store group"
      );
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameConfirm = async (newName, status = targetGroupStatus) => {
    const nameChanged = newName !== targetGroup;
    const deactivating =
      status === STATUS.INACTIVE && targetGroupStatus !== STATUS.INACTIVE;
    if (!newName || (!nameChanged && status === targetGroupStatus)) {
      return;
    }

    if (deactivating) {
      try {
        const { data } = await client.query({
          query: listCampaignsByStoreGroups,
          variables: { storeGroups: [targetGroup] },
          fetchPolicy: "network-only",
        });
        if (data?.listCampaignsByStoreGroups?.length > 0) {
          setPendingDeactivation({ newName, status });
          return;
        }
      } catch (error) {
        showToast(
          "error",
          "Status Update Failed",
          error.message || "Could not check for live campaigns"
        );
        return;
      }
    }

    await applyGroupUpdate(newName, status);
  };

  const handleConfirmDeactivation = async () => {
    const pending = pendingDeactivation;
    setPendingDeactivation(null);
    await applyGroupUpdate(pending.newName, pending.status);
  };

  const handleViewGroup = useCallback(
    (groupName) => {
      handlers.handleFilterChange({ ...filterData, categories: [groupName] });
      setViewedGroup(groupName);
      setActiveView([VIEW_OPTIONS[0]]);
    },
    [handlers, filterData]
  );

  // The batch update replaces each store's full categories list. Stores whose
  // new list is the same share one call; a null list leaves the store as is.
  const updateStoreCategories = async (storeIds, nextCategories) => {
    // Read each store's current categories from the stores already loaded.
    const storeById = new Map((allStores || []).map((s) => [s.id, s]));

    const batchesByCategories = new Map();
    storeIds.forEach((storeId) => {
      const categories = nextCategories(storeById.get(storeId));
      if (!categories) return;
      const key = [...categories].sort().join("|");
      const batch = batchesByCategories.get(key) || { categories, ids: [] };
      batch.ids.push(storeId);
      batchesByCategories.set(key, batch);
    });

    await Promise.all(
      [...batchesByCategories.values()].map(({ categories, ids }) =>
        executeUpdateCategories({
          variables: {
            input: {
              keywordGroupIds: ids,
              type: "OUTLET",
              categories,
            },
          },
        })
      )
    );
  };

  const handleSaveGroup = async (groupNames, storeIds) => {
    try {
      const groupsToAdd = Array.isArray(groupNames) ? groupNames : [groupNames];

      // Merge the new groups into each store's existing groups to keep this a pure "add".
      await updateStoreCategories(storeIds, (store) => [
        ...new Set([...storeCategories(store), ...groupsToAdd]),
      ]);

      showToast(
        "success",
        "Store Group Saved",
        `Stores have been added to group "${groupsToAdd.join(", ")}"`
      );

      if (refetch) refetch();
      refetchStoreGroups();
    } catch (error) {
      showToast("error", "Error", error.message || "Failed to update store group");
    }
  };

  const handleRemoveFromGroup = async (groupName, storeIds) => {
    try {
      await updateStoreCategories(storeIds, (store) =>
        store
          ? storeCategories(store).filter((category) => category !== groupName)
          : null
      );

      showToast(
        "success",
        "Removed From Group",
        `${storeIds.length} store${storeIds.length > 1 ? "s" : ""} removed from group "${groupName}"`
      );

      if (refetch) refetch();
      refetchStoreGroups();
    } catch (error) {
      showToast(
        "error",
        "Error",
        error.message || "Failed to remove stores from the group"
      );
    }
  };

  return (
    <div className="container">
      <div className="wrap">
        {groupInView && (
          <Breadcrumb
            breadcrumbs={[
              { label: "Store Management", href: "/store-management" },
              { label: "Store Groups", href: STORE_GROUPS_VIEW_URL },
              { label: groupInView, href: "#" },
            ]}
            activeIndex={2}
          />
        )}
        <div className="store-management-container">
          <div className="page-header">
            <h3 className="page-title">
              {groupInView ||
                `Available ${currentView === "stores" ? "Stores" : "Store Groups"}`}
            </h3>
            <div className="page-header-actions">
              <div className="view-switcher-inline">
                <Dropdown
                  childList={VIEW_OPTIONS}
                  selection="single"
                  selectedValue={activeView}
                  placeholder="Select View"
                  size="medium"
                  setSelectedValue={handleViewChange}
                />
              </div>
            </div>
          </div>

          {currentView === "stores" ? (
            <StoresListView
              stores={stores}
              allStores={allStores}
              storeGroups={storeGroups}
              loading={loading}
              searchTerm={searchTerm}
              filterData={filterData}
              handlers={handlers}
              onAddSingleStore={handleAddSingleStore}
              onImportFile={handleImportFile}
              onEditStore={handleEditStore}
              onViewStore={handleViewStore}
              onDeleteStore={handleDeleteStore}
              onDeleteMultiple={handleDeleteMultiple}
              onSaveGroup={handleSaveGroup}
              isSyncMarket={isSyncMarket}
              viewedGroup={groupInView}
              onRemoveFromGroup={handleRemoveFromGroup}
            />
          ) : (
            <StoreGroupsListView
              storeGroups={storeGroups}
              loading={storeGroupsLoading}
              statusFilter={groupStatusFilter}
              onStatusFilterChange={setGroupStatusFilter}
              onViewGroup={handleViewGroup}
              onAddGroup={handleAddGroup}
              onEditGroup={handleEditGroup}
              showToast={showToast}
              refetchStoreGroups={refetchStoreGroups}
              refetchProgramConfig={refetchProgramConfig}
              isSyncMarket={isSyncMarket}
            />
          )}
        </div>

        <StoreFileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => setShowFileUploadModal(false)}
          onSuccess={handleFileUploadSuccess}
        />
        <StoreGroupModal
          isOpen={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          onSave={handleSaveGroup}
          onEditGroup={handleEditGroup}
          onGroupCreated={refetchStoreGroups}
          defaultMode={currentView === "store-groups" ? "new" : "existing"}
        />

        <RenameStoreGroupModal
          isOpen={renameModalOpen}
          targetGroup={targetGroup}
          targetStatus={targetGroupStatus}
          onClose={handleRenameClose}
          onConfirm={handleRenameConfirm}
          loading={isRenaming}
          isSyncMarket={isSyncMarket}
        />

        <DeleteConfirmationModal
          isOpen={Boolean(pendingDeactivation)}
          title="Deactivate store group?"
          message="This store group is mapped to a live campaign. Are you sure to deactivate?"
          confirmText="Deactivate"
          onConfirm={handleConfirmDeactivation}
          onCancel={() => setPendingDeactivation(null)}
        />

        <ToastNotification
          {...toast}
          handleClose={closeToast}
          sx={{ zIndex: 9999 }}
        />
      </div>
    </div>
  );
}
